import React from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Helper — supported purities from a Making Style doc's rate rows.
const supportedPurities = (msDoc) => (msDoc?.rates || [])
  .map(r => r?.purity).filter(Boolean);

/**
 * Making Style Card — verified-tag cascade controls surfaced on Step 1 of
 * the Estimate wizard.
 *
 * Purely presentational: owns no state. Reads current form values +
 * override flags from props and delegates every mutation through the
 * three callbacks (`onSelectMakingStyle`, `onSelectPurityChip`, `onReset`)
 * plus `setForm` / `setWastageOverridden` / `setMakingOverridden` for the
 * inline wastage + making inputs.
 *
 * Extracted from EstimateFormDialog.jsx to keep the parent under the
 * 700-line guideline. See PRD.md → "Making Style Card" for the feature
 * spec and cascade formula.
 */
export const MakingStyleCard = ({
  makingStyles,
  currentMakingStyle,
  currentMakingStyleId,
  verifiedAnchor,
  form,
  setForm,
  calcNetWeight,
  wastageOverridden,
  makingOverridden,
  netWeightOverridden,
  setWastageOverridden,
  setMakingOverridden,
  onSelectMakingStyle,      // (msDoc) => void — full cascade
  onSelectPurityChip,       // (purity: string) => void — cascade within same style
  onReset,                  // () => void — restore verified baseline
  toast,
}) => {
  if (!verifiedAnchor) return null;

  const showReset = (
    currentMakingStyleId !== verifiedAnchor.msId
    || wastageOverridden
    || makingOverridden
    || netWeightOverridden
  );

  return (
    <div
      className="border-2 border-indigo-300 bg-indigo-50 rounded-lg p-3 space-y-2"
      data-testid="making-style-card"
    >
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-bold uppercase tracking-wide text-indigo-800">
          Making Style · Cascade Controls
        </Label>
        {showReset && (
          <button
            type="button"
            onClick={onReset}
            data-testid="reset-to-verified-btn"
            className="text-[11px] text-indigo-700 underline hover:text-indigo-900 font-medium"
          >
            Reset to verified data
          </button>
        )}
      </div>

      {/* Making Style dropdown — picking a different style triggers a full
          cascade including a possible purity snap. */}
      <Select
        value={currentMakingStyleId || ''}
        onValueChange={(id) => {
          const doc = makingStyles.find(m => (m.id || m._id) === id);
          if (doc) onSelectMakingStyle(doc);
        }}
      >
        <SelectTrigger className="bg-white text-sm py-5" data-testid="making-style-select-trigger">
          <SelectValue placeholder="Select Making Style" />
        </SelectTrigger>
        <SelectContent>
          {makingStyles.map(m => (
            <SelectItem key={m.id || m._id} value={m.id || m._id}>
              {m.name}
              {supportedPurities(m).length > 0 && (
                <span className="text-[10px] text-gray-500 ml-2">
                  ({supportedPurities(m).join('/')})
                </span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Purity strip — clickable within the current Making Style. */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] text-indigo-700 font-medium">Purity:</span>
        {['22K', '18K', '14K', '9K'].map(p => {
          const supported = supportedPurities(currentMakingStyle).includes(p);
          const selected  = form.goldPurity === p;
          return (
            <button
              key={p}
              type="button"
              disabled={!supported}
              data-testid={`ms-card-purity-chip-${p}`}
              title={supported ? `Switch to ${p}` : `${p} not supported by ${currentMakingStyle?.name || 'this style'}`}
              onClick={() => {
                if (!supported) {
                  toast({
                    title: `${p} not supported`,
                    description: `"${currentMakingStyle?.name || 'This style'}" only supports ${supportedPurities(currentMakingStyle).join(', ') || 'no purities'}.`,
                  });
                  return;
                }
                onSelectPurityChip(p);
              }}
              className={`text-xs px-2 py-1 rounded-full border font-semibold transition-all ${
                selected ? 'bg-indigo-600 text-white border-indigo-600'
                : supported ? 'bg-white text-indigo-700 border-indigo-300 hover:border-indigo-500 cursor-pointer'
                : 'bg-gray-100 text-gray-400 border-gray-200 line-through cursor-not-allowed'
              }`}
            >
              {p}
            </button>
          );
        })}
      </div>

      {/* Resolved values row — inline-editable. */}
      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-indigo-200">
        <div>
          <div className="text-[10px] text-indigo-600 font-medium uppercase flex items-center gap-1">
            Wastage {wastageOverridden && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500" title="Modified" data-testid="ms-card-wastage-dot" />
            )}
          </div>
          <div className="flex items-baseline gap-0.5">
            <input
              type="number"
              step="0.1"
              inputMode="decimal"
              value={form.wastagePercentage}
              onChange={e => { setWastageOverridden(true); setForm(f => ({ ...f, wastagePercentage: e.target.value })); }}
              className="w-14 bg-transparent text-sm font-bold text-indigo-900 border-b border-indigo-300 focus:outline-none focus:border-indigo-600 px-0"
              data-testid="ms-card-wastage"
            />
            <span className="text-sm font-bold text-indigo-900">%</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] text-indigo-600 font-medium uppercase flex items-center gap-1">
            Making {makingOverridden && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500" title="Modified" data-testid="ms-card-making-dot" />
            )}
          </div>
          <div className="flex items-baseline gap-0.5">
            <span className="text-sm font-bold text-indigo-900">₹</span>
            {form.makingChargesType === 'per_gram' ? (
              <>
                <input
                  type="number"
                  inputMode="numeric"
                  value={form.makingChargesPerGram}
                  onChange={e => { setMakingOverridden(true); setForm(f => ({ ...f, makingChargesPerGram: e.target.value })); }}
                  className="w-16 bg-transparent text-sm font-bold text-indigo-900 border-b border-indigo-300 focus:outline-none focus:border-indigo-600 px-0"
                  data-testid="ms-card-making"
                />
                <span className="text-xs text-indigo-800">/g</span>
              </>
            ) : (
              <>
                <input
                  type="number"
                  inputMode="numeric"
                  value={form.fixedMakingCharge}
                  onChange={e => { setMakingOverridden(true); setForm(f => ({ ...f, fixedMakingCharge: e.target.value })); }}
                  className="w-20 bg-transparent text-sm font-bold text-indigo-900 border-b border-indigo-300 focus:outline-none focus:border-indigo-600 px-0"
                  data-testid="ms-card-making"
                />
                <span className="text-xs text-indigo-800">flat</span>
              </>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-indigo-600 font-medium uppercase flex items-center gap-1">
            Net Wt {netWeightOverridden && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-yellow-500" title="Modified" data-testid="ms-card-netwt-dot" />
            )}
          </div>
          <div className="text-sm font-bold text-indigo-900" data-testid="ms-card-netwt">
            {calcNetWeight.toFixed(2)}g
          </div>
        </div>
      </div>

      {/* Verified anchor footer */}
      <div className="text-[10px] text-indigo-700/70 pt-1 border-t border-indigo-200" data-testid="ms-card-anchor">
        Verified anchor: {verifiedAnchor.purity} · {verifiedAnchor.netWeight?.toFixed(2)}g · {verifiedAnchor.msName || 'Making Style'}
      </div>
    </div>
  );
};

export default MakingStyleCard;
